import { v4 as uuidv4 } from 'uuid';
import {
    RegulatoryReportingDescription,
    PaymentRequestStatus,
    RequestAction,
    RequestStatus,
    CashMovementActivityTypeFilter
} from '../constants/request';
import type { InstructionTypeCode } from '../domain/InstructionType';
import type { CashMovementBalance } from '../sites/cmr/domain/CashMovementBalance';
import type { Breaches } from '../sites/cmr/domain/Breaches';
import { Position } from './position';
import { UserDetails } from './user';

export type TabValue = 'latest' | 'all';

export enum PaymentType {
    ACHPull,
    Withdrawal
}

export interface AccountChangeRequest {
    _id: string;
    requestRef: number;
    status: RequestStatus;
    requestDate: string;
    sourceAccount: string;
    destinationAccount: string;
    reason?: string;
    description?: string;
    rejectionReason?: string | null;
    statusChangedDate: string;
    positions: Position[];
    neonUserId: string;
    requestor: UserDetails | undefined;
    canceller: UserDetails | undefined;
}

export interface RiskLimitsRequest {
    _id: string;
    requestRef: number;
    status: RequestStatus;
    platform: string;
    accountNumber: string;
    accountName: string;
    reason?: string;
    description?: string;
    rejectionReason?: string | null;
    updatedAt: string;
    neonUserId: string;
    requestDate: string;
    contractLimits: {
        contractId: string;
        contractName: string;
        productName: string;
        exchange: string;
        symbol: string;
        instrumentType: string;
        oldLimitPerCommodity?: string;
        oldLimitPerInstrument?: string;
        oldLimitPerContract?: string;
        oldTotalSizeLimit?: string;
        limitPerCommodity: string;
        limitPerInstrument: string;
        limitPerContract: string;
        totalSizeLimit: string;
        action: RequestAction;
    }[];
    accountLimits: {
        dailyLossLimit: string;
        marginPercentage: string;
        oldDailyLossLimit?: string;
        oldMarginPercentage?: string;
        oldSingleTradeSizeLimit?: string;
        singleTradeSizeLimit: string;
    };
    requester: UserDetails | undefined;
    canceller: UserDetails | undefined;
}

export interface AccountSetupRequest {
    id: string;
    requestRef: number;
    neonUserEmail: string;
    clientName: string;
    accountType: string;
    submissionDate: Date;
    updated: Date;
    message?: string;
    status: RequestStatus;
    applicationStatus: string;
}

export interface PaginatedResponse<T> {
    data: T[];
    paginationInfo: PaginationInfo;
}

export interface CashMovementActivityResponse extends PaginatedResponse<CashMovementActivity> {
    receiptsAvailable: boolean;
}

export interface PaginationInfo {
    pageNumber: number;
    pageSize: number;
    totalRecords?: number;
    totalPages?: number;
    hasMoreRecords?: boolean;
}

export interface CashMovementRequest {
    accountName: string;
    accountNumber: string;
    amount: number;
    currencyCode: string;
    id: string;
    justification: string;
    instructionTypeCode: InstructionTypeCode;
    requestedAt: Date;
    requestedBy: string;
    requestedByDisplayName: string;
    standardSettlementInstructionAlias: string;
    status: PaymentRequestStatus;
    updatedAt: Date;
    usdEquivalent: number;
    usdFxRate: string;
}

export interface CashMovementActivityBase {
    activityId: string;
    reference: string;
    accountNumber: string;
    amount: number;
    currency: string;
    status: PaymentRequestStatus | ReceiptAllocationStatus;
    activityDate: Date;
}

export interface PaymentActivity extends CashMovementActivityBase {
    activityType: 'PaymentRequest';
    paymentRequest: CashMovementRequest;
}

export interface ReceiptAllocation extends CashMovementActivityBase {
    activityType: 'Receipt';
    paymentRequest?: null;
}

export interface ReceiptAllocationDetail {
    activityId: string;
    receiptReference: string;
    accountNumber: string;
    allocatedAmount: number;
    usdEquivalent?: number | null;
    usdFxRate?: number | null;
    currency: string;
    allocatedAt: Date;
}

export type CashMovementActivity = PaymentActivity | ReceiptAllocation;

export interface CashMovementBalanceResponse {
    updated: Date;
    reference: number;
    user: string;
    ccy: string;
    amount: number;
    masterAccountCurrencyAmount: number;
    status: string;
    account: string;
    masterAccountCurrencyFxRate: string;
    isJustificationRequired: boolean;
    justificationRequiredText: string;
    regulatoryReportingDescription: RegulatoryReportingDescription;
    balancesInMasterCurrency: CashMovementBalance[];
    balancesInSubAccountCurrency: CashMovementBalance[];
    breaches: Breaches;
}

export interface CreateGeneralSupportRequest {
    type: string;
    subject: string;
    message: string;
}

export interface CreateCashMovementRequest {
    accountNumber: string;
    amount: number;
    templateId?: typeof uuidv4;
    alias?: string;
    currencyCode?: string;
}

export interface ValidateCashMovement extends CreateCashMovementRequest {
    id: string;
}

export type ValidateCashMovementRequest = ValidateCashMovement[];

export type CreateBulkCashMovementsRequest = CreateCashMovementRequest[];

export interface ValidatePaymentRequestResponse {
    paymentRequest: CashMovementRequest;
    errors: string[];
    errorCanBeBypassed?: boolean;
    ssiAliasMatches: string[];
}

export type BulkSubmitSummary = {
    totalRows: number;
    uploadedRows: number;
    discardedRows: number;
    manualSsiRows: number;
    reportData: BulkReportRow[];
};

export interface BulkReportRow {
    account: string;
    currency: string;
    amount: number;
    alias: string;
    status: 'uploaded' | 'discarded';
}

export interface CreateGeneralSupportMessage {
    id: string;
    message: string;
}

export type Sender = 'NeonUser' | 'NeonConcierge';
export interface Message {
    id: string;
    message: string;
    sender: Sender;

    createdOn: string;
}

export interface GeneralSupportRequest extends CreateGeneralSupportRequest {
    requestRef: string;
    id: string;
    neonUserId: string;
    messages?: Message[];
    requestDate: string;
    updatedAt: string;
    status: RequestStatus;
    unread: boolean;
}

export interface Account {
    businessDate: string;
    currency: string;
    family: string;
    firm: string;
    id: string;
    location: string;
    name: string;
    salesman: string;
    status: string;
}

export interface StandardSettlementInstruction {
    templateId: typeof uuidv4;
    accountNumber: string;
    currency: string;
    alias: string;
    name: string | null;
    customerLedger: string;
}

export interface DateRange {
    startDate: Date | string;
    endDate: Date | string;
}

export interface RequestQuery {
    onlyUserRequests?: boolean;
    statuses?: string[];
    dateRange?: DateRange;
    selectedAccounts?: string[];
    activityTypes?: CashMovementActivityTypeFilter[];
}

export enum SortDirection {
    'ASC' = 'ASC',
    'DESC' = 'DESC'
}
export interface SortingInfo {
    sortingBy: string;
    sortDirection: SortDirection;
}

export interface PaginatedRequest {
    paginationInfo?: PaginationInfo;
    requestQuery?: RequestQuery;
    sortingInfo?: SortingInfo;
}

export interface LatestRequest {
    reference: string;
    status: RequestStatus | PaymentRequestStatus;
    type: 'AccountChangeRequest' | 'GeneralRequest' | 'KybRequest' | 'PaymentRequest' | 'Receipt';
    date: Date;
    url?: string | null;
}
